'use es6';

import * as CommonListSegStrategy from './CommonListSegStrategy';
import * as DSAssetFamilies from 'customer-data-filters/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { DSFilterFromListSegClassic, DSFilterToListSegClassic } from '../../converters/Converters';
import { FilterFamilyAndButtonTooltipTranslator, FilterFamilyGroupHeadingTranslator, FilterFamilyNameTranslator } from '../FilterFamilyTranslator';
import { ListSegClassic } from 'customer-data-filters/filterQueryFormat/strategy/StrategyTypes';
import { OrderedMap, Set as ImmutableSet, is } from 'immutable';
import { __ANY_LINK, __ANY_PAGE } from 'customer-data-filters/converters/listSegClassic/ListSegConstants';
import { getListSegClassicOperatorsForType } from 'customer-data-filters/converters/listSegClassic/ListSegClassicTypeToOperator';
import { getListSegFilterFamilyOptions } from 'customer-data-filters/converters/listSegClassic/ListSegClassicFilterFamilies';
import { parseDynamicFilterFamilyId } from '../../filterQueryFormat/DynamicFilterFamilies';
import { testDynamicFilterFamily } from '../../filterQueryFormat/DynamicFilterFamilies';
import DSFilterStrategyRecord from 'customer-data-filters/filterQueryFormat/strategy/DSFilterStrategyRecord';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import enviro from 'enviro';
import getIn from 'transmute/getIn';
import unescapedText from 'I18n/utils/unescapedText';
import { jsx as _jsx } from "react/jsx-runtime";
const getIntegrationsFromData = (integrations = OrderedMap()) => {
  return integrations.reduce((acc, integration) => acc.set(integration.get('type'), integration), OrderedMap()).sort((integrationA, integrationB) => integrationA.get('label').localeCompare(integrationB.get('label')));
};
const getIntegrationLabel = (filterFamily, integrations) => {
  const integration = integrations ? integrations.get(parseDynamicFilterFamilyId(DSAssetFamilies.INTEGRATION, filterFamily)) : undefined;
  return integration ? integration.get('label') : unescapedText(`customerDataFilters.DSAssetFamilyNames.${DSAssetFamilies.INTEGRATION.toUpperCase()}`);
};
export const onUpdate = (prevProps, props) => {
  const {
    data,
    fetchMore
  } = props;
  const prevCtaGuid = prevProps.data.get(DSAssetFamilies.CTA_TO_PLACEMENT);
  const newCtaGuids = data.get(DSAssetFamilies.CTA_TO_PLACEMENT);
  if (!is(prevCtaGuid, newCtaGuids)) {
    const ctaIds = newCtaGuids.map(getIn(['metadata', 'placementGuid'])).filter(id => !data.hasIn([DSAssetFamilies.CTA, id]));
    if (ctaIds.size > 0) {
      fetchMore({
        variables: {
          assetFamily: DSAssetFamilies.CTA,
          batchUpdate: true,
          ids: ctaIds.toArray()
        }
      });
    }
  }
};
export const getField = (assetFamily, id, defaultField, props) => {
  const {
    data,
    fetchMore
  } = props;
  const commonField = CommonListSegStrategy.getField(assetFamily, id, defaultField, props);
  if (commonField) {
    return commonField;
  }
  if (assetFamily === DSAssetFamilies.CTA_TO_PLACEMENT) {
    // Fetch missing CTA to placement conversions. Note that the
    // reference-resolver automatically batches and deduplicates requests.
    // https://git.hubteam.com/HubSpot/customer-data-filters/pull/184
    if (!getIn([DSAssetFamilies.CTA_TO_PLACEMENT, id], data)) {
      fetchMore({
        variables: {
          assetFamily: DSAssetFamilies.CTA_TO_PLACEMENT,
          batchUpdate: true,
          ids: [id]
        }
      });
    }
    const placementId = getIn([assetFamily, id, 'metadata', 'placementGuid'], data);
    return getIn([DSAssetFamilies.CTA, placementId], data) || defaultField;
  }
  return undefined;
};
export const getFilterFamilyAndButtonTooltip = (filterFamily, props) => {
  const {
    data
  } = props;
  if (testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, filterFamily)) {
    return getIntegrationLabel(filterFamily, data.get(DSAssetFamilies.INTEGRATION));
  } else if (filterFamily === DSAssetFamilies.LINE_ITEM_PROPERTY) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataFilters.FilterListSegCustomFamily.LINE_ITEM.tooltip"
    });
  }
  return FilterFamilyAndButtonTooltipTranslator(filterFamily);
};
export const getFilterFamilyGroupHeading = (filterFamily, props) => {
  const {
    data
  } = props;
  if (testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, filterFamily)) {
    const integrationLabel = getIntegrationLabel(filterFamily, data.get(DSAssetFamilies.INTEGRATION));
    return I18n.text(`customerDataFilters.FilterFamilyGroupHeadingTranslator.${DSAssetFamilies.INTEGRATION}`, {
      entityName: integrationLabel
    });
  } else if (filterFamily === DSAssetFamilies.LINE_ITEM_PROPERTY) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataFilters.FilterListSegCustomFamily.LINE_ITEM.heading"
    });
  }
  return FilterFamilyGroupHeadingTranslator(filterFamily);
};
export const getFilterFamilyName = (filterFamily, props) => {
  const {
    data
  } = props;
  if (testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, filterFamily)) {
    return getIntegrationLabel(filterFamily, data.get(DSAssetFamilies.INTEGRATION));
  } else if (filterFamily === DSAssetFamilies.LINE_ITEM_PROPERTY) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataFilters.FilterListSegCustomFamily.LINE_ITEM.name"
    });
  }
  return FilterFamilyNameTranslator(filterFamily);
};
export const getFilterFamilyOptions = props => {
  const {
    data,
    getIsUngated,
    hasScope
  } = props;

  // Mothra: hubspot.ads.timeline.{adwords,facebook,linkedin}.eventTypeId
  const filterFamilyIntAdwords = enviro.isProd() ? `${DSAssetFamilies.INTEGRATION}-425` : `${DSAssetFamilies.INTEGRATION}-492`;
  const filterFamilyIntFacebook = enviro.isProd() ? `${DSAssetFamilies.INTEGRATION}-15300` : `${DSAssetFamilies.INTEGRATION}-12672`;
  const filterFamilyIntLinkedin = enviro.isProd() ? `${DSAssetFamilies.INTEGRATION}-2285` : `${DSAssetFamilies.INTEGRATION}-2780`;
  const filterFamilyBlocklist = ImmutableSet.of(filterFamilyIntAdwords, filterFamilyIntFacebook, filterFamilyIntLinkedin);
  const isEnabled = {
    [DSAssetFamilies.ADS_PROPERTY]: {
      enabled: {
        metadata: {
          value: true
        }
      }
    },
    [DSAssetFamilies.SURVEY_MONKEY_SURVEY]: data.get(DSAssetFamilies.SURVEY_MONKEY_SETTING),
    [DSAssetFamilies.SURVEY_MONKEY_QUESTION]: data.get(DSAssetFamilies.SURVEY_MONKEY_SETTING),
    [DSAssetFamilies.GOTO_WEBINAR_WEBINAR]: data.get(DSAssetFamilies.GOTO_WEBINAR_SETTING)
  };
  return getListSegFilterFamilyOptions({
    blocklist: filterFamilyBlocklist,
    fixedValues: getIntegrationsFromData(data.get(DSAssetFamilies.INTEGRATION)),
    hasGate: getIsUngated,
    hasScope,
    isEnabled: name => getIn([name, 'enabled', 'metadata', 'value'], isEnabled)
  });
};
const serializer = {
  toFormat: DSFilterToListSegClassic,
  fromFormat: DSFilterFromListSegClassic
};
export default DSFilterStrategyRecord({
  onMount: CommonListSegStrategy.onMount,
  onUpdate,
  getField,
  getFieldDefinitions: CommonListSegStrategy.getFieldDefinitions,
  getFieldGroups: CommonListSegStrategy.getFieldGroups,
  getFields: CommonListSegStrategy.getFields,
  getFilterFamilyAndButtonTooltip,
  getFilterFamilyGroupHeading,
  getFilterFamilyOptions,
  getFilterFamilyName,
  getOperatorsForType: getListSegClassicOperatorsForType,
  getReferenceResolvers: CommonListSegStrategy.getReferenceResolvers,
  getSpecialFieldOptions: CommonListSegStrategy.getSpecialFieldOptions,
  serializer,
  type: ListSegClassic
});